import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { queryKeys } from '../../lib/queryKeys.js';
import { Sheet } from '../ui/Sheet.jsx';
import { useUi } from '../../stores/ui.js';
import { useSession } from '../../stores/session.js';
import { cn } from '../../lib/format.js';

/**
 * The mobile navigation sheet.
 *
 * Two levels: a world opens its categories in place rather than pushing a new
 * screen, because a back-and-forth drill-down on a phone loses people faster
 * than a slightly longer list.
 */

export const MobileNav = () => {
  const open = useUi((state) => state.menuOpen);
  const close = useUi((state) => state.closeMenu);
  const sessionStatus = useSession((state) => state.status);
  const [expanded, setExpanded] = useState(null);

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories.list({ tree: true, activeOnly: true }),
    queryFn: () => api.get('/categories', { params: { tree: 'true', activeOnly: 'true' } }),
    staleTime: 10 * 60 * 1000,
    enabled: open,
  });

  const worlds = [
    { key: 'fragrance', label: 'Fragrance' },
    { key: 'sport', label: 'Sport' },
  ];

  return (
    <Sheet open={open} onClose={close} side="left" title="Menu">
      <nav aria-label="Mobile" className="px-2 py-3">
        {worlds.map((world) => {
          const roots = (categories ?? []).filter((category) => category.world === world.key);

          return (
            <div key={world.key} data-world={world.key} className="border-b border-mist last:border-0">
              <div className="flex items-center">
                <Link
                  to={`/shop?world=${world.key}`}
                  onClick={close}
                  className="flex-1 px-3 py-3.5 font-display text-base tracking-[-0.02em] text-ink"
                >
                  {world.label}
                </Link>
                {roots.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === world.key ? null : world.key)}
                    aria-expanded={expanded === world.key}
                    aria-label={`${expanded === world.key ? 'Collapse' : 'Expand'} ${world.label}`}
                    className="grid size-11 place-items-center rounded text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  >
                    <svg
                      viewBox="0 0 16 16"
                      className={cn('size-4 transition-transform duration-200', expanded === world.key && 'rotate-45')}
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M7.25 2.75h1.5v4.5h4.5v1.5h-4.5v4.5h-1.5v-4.5h-4.5v-1.5h4.5z" />
                    </svg>
                  </button>
                )}
              </div>

              {expanded === world.key && (
                <ul className="pb-3">
                  {roots.map((root) => (
                    <li key={root._id}>
                      <Link
                        to={`/category/${root.slug}`}
                        onClick={close}
                        className="block px-3 py-2 text-xs font-medium text-ink"
                      >
                        {root.name}
                      </Link>
                      {root.children?.length > 0 && (
                        <ul className="mb-1 ml-3 border-l border-mist">
                          {root.children.map((child) => (
                            <li key={child._id}>
                              <Link
                                to={`/category/${child.slug}`}
                                onClick={close}
                                className="block py-1.5 pl-3 text-xs text-muted"
                              >
                                {child.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        <ul className="mt-2 border-t border-mist pt-2">
          {[
            { label: 'New in', to: '/shop?isNewArrival=true' },
            { label: 'Everything', to: '/shop' },
            { label: 'Brands', to: '/brands' },
            { label: 'Journal', to: '/journal' },
            { label: 'Track an order', to: '/orders/track' },
            { label: 'Contact us', to: '/contact' },
            {
              label: sessionStatus === 'authenticated' ? 'Your account' : 'Sign in',
              to: sessionStatus === 'authenticated' ? '/account' : '/sign-in',
            },
          ].map((link) => (
            <li key={link.to}>
              <Link to={link.to} onClick={close} className="block px-3 py-3 text-sm text-ink">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </Sheet>
  );
};

export default MobileNav;
